import React, { useRef, useState } from 'react';
import { Upload, FolderOpen, Users, X, ArrowRight, ScanSearch, ImageOff, RotateCcw } from 'lucide-react';
import { uploadDataUriToBackend } from '../services/backendService';
import { UserAssetsModal } from './wizard/UserAssetsModal';
import { ActorsLibraryModal } from './wizard/ActorsLibraryModal';

const TYPE_LABEL = { character: 'Character', costume: 'Costume', element: 'Element', prop: 'Prop', location: 'Location' };

const readAsDataUrl = (file) => new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onloadend = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
});

const smallBtn = 'flex-1 py-1.5 border border-dashed border-gray-600 rounded-md hover:border-purple-500/60 text-[10px] flex items-center justify-center gap-1 text-gray-400 hover:text-gray-200 disabled:opacity-40 transition-colors';

const SwapRow = ({ item, replacement, onPick, onClear, onOpenPicker, uploading, disabled, onToggleScreenshot }) => {
    const fileRef = useRef(null);
    return (
        <div className={`rounded-lg p-2.5 space-y-2 border ${replacement ? 'bg-purple-900/20 border-purple-500/40' : 'bg-gray-800/60 border-gray-700/80'}`} data-testid={`swap-item-${item.key}`}>
            <div className="flex items-center gap-2">
                <div className="w-12 h-12 rounded-md overflow-hidden border border-gray-700 bg-gray-900 shrink-0 relative group/shot">
                    {item.imageUrl && <img src={item.imageUrl} alt={item.name} className="w-full h-full object-cover" crossOrigin="anonymous" referrerPolicy="no-referrer" data-testid={`swap-screenshot-${item.key}`} />}
                    {item.screenshotRemoved && (
                        <div className="w-full h-full flex flex-col items-center justify-center text-gray-500" data-testid={`swap-text-only-${item.key}`}>
                            <ImageOff className="w-4 h-4" /><span className="text-[8px] mt-0.5">Text only</span>
                        </div>
                    )}
                    {onToggleScreenshot && (item.imageUrl || item.screenshotRemoved) && (
                        <button onClick={() => onToggleScreenshot(item.detectedId)} disabled={disabled}
                            className={`absolute top-0 right-0 w-4 h-4 flex items-center justify-center rounded-bl text-white transition-opacity ${item.screenshotRemoved ? 'bg-purple-600 opacity-100' : 'bg-black/70 hover:bg-red-600 opacity-90'}`}
                            title={item.screenshotRemoved ? 'Restore screenshot' : 'Remove screenshot — send description only'}
                            data-testid={`swap-toggle-screenshot-${item.key}`}>
                            {item.screenshotRemoved ? <RotateCcw className="w-2.5 h-2.5" /> : <X className="w-2.5 h-2.5" />}
                        </button>
                    )}
                </div>
                <ArrowRight className={`w-3.5 h-3.5 shrink-0 ${replacement ? 'text-purple-400' : 'text-gray-700'}`} />
                <div className="w-12 h-12 rounded-md overflow-hidden border border-dashed border-gray-600 bg-gray-900 shrink-0 relative">
                    {replacement?.url && <img src={replacement.url} alt="Replacement" className="w-full h-full object-cover" crossOrigin="anonymous" referrerPolicy="no-referrer" data-testid={`swap-replacement-${item.key}`} />}
                    {uploading && <div className="absolute inset-0 flex items-center justify-center bg-black/60"><span className="w-3 h-3 border border-white/40 border-t-white rounded-full animate-spin" /></div>}
                </div>
                <div className="flex-1 min-w-0">
                    <p className="text-xs text-gray-200 truncate" title={item.name}>{item.name}</p>
                    <p className="text-[10px] text-gray-500">{TYPE_LABEL[item.type] || 'Item'}{Number.isFinite(item.timestampSec) ? ` · ${item.timestampSec}s` : ''}{item.matchedName && item.matchedName !== item.name ? ` · ${item.matchedName}` : ''}</p>
                    {item.type === 'character' && replacement?.assetId && <p className="text-[9px] text-emerald-400" data-testid={`swap-asset-badge-${item.key}`}>Seedance asset</p>}
                </div>
                {replacement && (
                    <button onClick={onClear} disabled={disabled} className="p-1 rounded hover:bg-red-600/20 text-gray-500 hover:text-red-400" title="Keep original" data-testid={`swap-clear-${item.key}`}>
                        <X className="w-3.5 h-3.5" />
                    </button>
                )}
            </div>
            {item.description && <p className="text-[10px] text-gray-400 line-clamp-2" title={item.description} data-testid={`swap-desc-${item.key}`}>{item.description}</p>}
            {item.screenshotRemoved && <p className="text-[10px] text-purple-300" data-testid={`swap-desc-only-note-${item.key}`}>Screenshot removed — only the description will be sent.</p>}
            <input ref={fileRef} type="file" accept="image/*" className="hidden" data-testid={`swap-file-${item.key}`}
                onChange={e => { const f = e.target.files?.[0]; e.target.value = ''; if (f) onPick(f); }} />
            <div className="flex gap-1.5">
                <button className={smallBtn} disabled={disabled || uploading} onClick={() => fileRef.current?.click()} data-testid={`swap-upload-${item.key}`}><Upload className="w-3 h-3" /> Upload</button>
                <button className={smallBtn} disabled={disabled || uploading} onClick={() => onOpenPicker('assets')} data-testid={`swap-user-assets-${item.key}`}><FolderOpen className="w-3 h-3" /> User assets</button>
                <button className={smallBtn} disabled={disabled || uploading} onClick={() => onOpenPicker('library')} data-testid={`swap-library-${item.key}`}><Users className="w-3 h-3" /> Library</button>
            </div>
        </div>
    );
};

export const SwapObjectsPanel = ({ items, replacements, setReplacements, note, setNote, disabled, source = 'cast', onAnalyse, detectState = {}, onToggleScreenshot }) => {
    const [picker, setPicker] = useState(null); // { key, kind }
    const [uploadingKey, setUploadingKey] = useState(null);
    const [error, setError] = useState(null);

    const setRep = (key, rep) => setReplacements(prev => {
        const next = { ...prev };
        if (rep) next[key] = rep; else delete next[key];
        return next;
    });

    const handleFile = async (key, file) => {
        if (!file.type.startsWith('image/')) { setError('Please choose an image file.'); return; }
        setError(null); setUploadingKey(key);
        try {
            const dataUrl = await readAsDataUrl(file);
            const url = await uploadDataUriToBackend(dataUrl, sessionStorage.getItem('userEmail') || '');
            setRep(key, { url, name: file.name, source: 'upload' });
        } catch (e) { setError(`Upload failed: ${e.message}`); }
        finally { setUploadingKey(null); }
    };

    const count = Object.keys(replacements).length;
    return (
        <div className="space-y-3" data-testid="tool-swap-objects">
            <h3 className="text-sm font-semibold text-gray-300 uppercase tracking-wide">Swap Objects &amp; Characters</h3>
            <p className="text-xs text-gray-500">Pick a replacement for anything you want to change. Only swapped items change; everything else stays exactly as it is.</p>
            {onAnalyse && (
                <div className="space-y-1.5">
                    <button onClick={onAnalyse} disabled={disabled}
                        className="w-full py-2 rounded-lg bg-purple-600/80 hover:bg-purple-600 disabled:bg-gray-700 text-white text-xs font-semibold flex items-center justify-center gap-1.5 transition-colors"
                        data-testid="swap-analyse-video">
                        {detectState.busy ? <span className="w-3 h-3 border border-white/40 border-t-white rounded-full animate-spin" /> : <ScanSearch className="w-3.5 h-3.5" />}
                        {detectState.busy ? (detectState.message || 'Analysing…') : source === 'detected' ? 'Re-analyse video' : 'Analyse video'}
                    </button>
                    <p className="text-[10px] text-gray-500" data-testid="swap-source">
                        {source === 'detected' ? `To be swapped — ${items.length} item${items.length === 1 ? '' : 's'} found in this video` : 'To be swapped — showing Cast & World items for this shot'}
                    </p>
                    {detectState.notice && <p className="text-[10px] text-amber-400" data-testid="swap-notice">{detectState.notice}</p>}
                </div>
            )}
            {items.length === 0 ? (
                <p className="text-xs text-gray-600 text-center py-6 border border-dashed border-gray-700 rounded-lg" data-testid="swap-empty">No characters, costumes or props are linked to this shot.</p>
            ) : (
                <div className="space-y-2">
                    {items.map(it => (
                        <SwapRow key={it.key} item={it} replacement={replacements[it.key]} disabled={disabled}
                            uploading={uploadingKey === it.key}
                            onToggleScreenshot={onToggleScreenshot}
                            onPick={(f) => handleFile(it.key, f)}
                            onClear={() => setRep(it.key, null)}
                            onOpenPicker={(kind) => setPicker({ key: it.key, kind })} />
                    ))}
                </div>
            )}
            <div>
                <label className="text-xs text-gray-400 font-medium">Extra instructions (optional)</label>
                <textarea value={note} onChange={e => setNote(e.target.value)} disabled={disabled}
                    placeholder="e.g. keep the new rider's helmet red"
                    className="w-full mt-1 p-2 bg-gray-800 border border-gray-700 rounded-lg resize-none h-14 text-xs text-gray-300 placeholder-gray-600 focus:border-purple-500 focus:outline-none" data-testid="swap-note" />
            </div>
            <p className="text-[11px] text-gray-500" data-testid="swap-count">{count} item{count === 1 ? '' : 's'} selected for swap</p>
            {error && <p className="text-[11px] text-red-400" data-testid="swap-error">{error}</p>}
            {picker?.kind === 'assets' && (
                <UserAssetsModal activeTab="all" onClose={() => setPicker(null)}
                    onSelect={(url, asset) => { if (url) setRep(picker.key, { url, name: asset?.name || null, source: 'user-assets' }); setPicker(null); }} />
            )}
            {picker?.kind === 'library' && (
                <ActorsLibraryModal onClose={() => setPicker(null)}
                    onSelect={(a) => {
                        if (a?.imageUrl) setRep(picker.key, {
                            url: a.imageUrl, name: a.name || null, source: 'library', actorId: a.actorId || null,
                            assetGroupId: a.byteplusAssetGroupId || null, assetId: a.byteplusAssetId || null,
                            useByteplusForSeedance: a.useByteplusForSeedance, assetStatus: a.byteplusAssetStatus || null,
                        });
                        setPicker(null);
                    }} />
            )}
        </div>
    );
};
